// ========================================
// Calendar Manager
// Wrapper for FullCalendar initialization and management
// ========================================

const CalendarManager = {
    instances: {},

    /**
     * Initialize a FullCalendar instance
     */
    initCalendar(elementId, options = {}) {
        const calendarEl = document.getElementById(elementId);

        if (!calendarEl) {
            console.error(`Calendar element #${elementId} not found`);
            return null;
        }

        if (typeof FullCalendar === 'undefined') {
            console.error('FullCalendar library not loaded');
            return null;
        }

        try {
            const defaultOptions = {
                initialView: 'timeGridWeek',
                headerToolbar: {
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay'
                },
                locale: 'es',
                buttonIcons: false,
                buttonText: { prev: '‹', next: '›', prevYear: '«', nextYear: '»' },
                slotMinTime: '08:00:00',
                slotMaxTime: '22:00:00',
                allDaySlot: false,
                height: 'auto',
                expandRows: true,
                selectable: true,
                selectMirror: true,
                editable: false,
                eventStartEditable: false,
                eventDurationEditable: false
            };

            const mergedOptions = { ...defaultOptions, ...options };
            const calendar = new FullCalendar.Calendar(calendarEl, mergedOptions);

            calendar.render();

            // Store instance
            this.instances[elementId] = calendar;

            return calendar;
        } catch (error) {
            console.error('Error initializing calendar:', error);
            return null;
        }
    },

    /**
     * Get calendar instance by element ID
     */
    getCalendarInstance(elementId) {
        return this.instances[elementId] || null;
    },

    /**
     * Add event to calendar
     */
    addEvent(elementId, event) {
        const calendar = this.getCalendarInstance(elementId);
        if (calendar) {
            calendar.addEvent(event);
        }
    },

    /**
     * Remove event from calendar
     */
    removeEvent(elementId, eventId) {
        const calendar = this.getCalendarInstance(elementId);
        if (calendar) {
            const event = calendar.getEventById(eventId);
            if (event) {
                event.remove();
            }
        }
    },

    /**
     * Update event in calendar
     */
    updateEvent(elementId, eventId, updates) {
        const calendar = this.getCalendarInstance(elementId);
        if (calendar) {
            const event = calendar.getEventById(eventId);
            if (event) {
                event.setProp('title', updates.title || event.title);
                if (updates.start) event.setStart(updates.start);
                if (updates.end) event.setEnd(updates.end);
                if (updates.backgroundColor) event.setProp('backgroundColor', updates.backgroundColor);
                if (updates.borderColor) event.setProp('borderColor', updates.borderColor);
            }
        }
    },

    /**
     * Remove all events from calendar
     */
    removeAllEvents(elementId) {
        const calendar = this.getCalendarInstance(elementId);
        if (calendar) {
            calendar.removeAllEvents();
        }
    },

    /**
     * Add event source to calendar
     */
    addEventSource(elementId, events) {
        const calendar = this.getCalendarInstance(elementId);
        if (calendar) {
            calendar.addEventSource(events);
        }
    },

    /**
     * Refresh calendar events
     */
    refreshEvents(elementId, events) {
        const calendar = this.getCalendarInstance(elementId);
        if (calendar) {
            calendar.removeAllEvents();
            calendar.addEventSource(events);
        }
    },

    /**
     * Go to specific date
     */
    gotoDate(elementId, date) {
        const calendar = this.getCalendarInstance(elementId);
        if (calendar) {
            calendar.gotoDate(date);
        }
    },

    /**
     * Change calendar view
     */
    changeView(elementId, viewName) {
        const calendar = this.getCalendarInstance(elementId);
        if (calendar) {
            calendar.changeView(viewName);
        }
    },

    /**
     * Destroy calendar instance
     */
    destroyCalendar(elementId) {
        const calendar = this.getCalendarInstance(elementId);
        if (calendar) {
            calendar.destroy();
            delete this.instances[elementId];
        }
    }
};

// Make CalendarManager globally available
window.CalendarManager = CalendarManager;
